import { AutocompleteInputChangeReason } from '@mui/material';
import { FormikValues } from 'formik';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { countryList } from '../../../constants/countries';
import { ISelectorValue } from '../../../types/shell';
import { findCountryOption } from '../../../util/address';
import { filterByAllowedCountries } from '../../../util/helpers';
import { Selector } from './Selector';

interface IProps extends FormikValues {
  label?: string;
  error?: string | string[] | null | undefined;
  required?: boolean;
  value?: string;
  name?: string;
  allowedCountries?: string[];
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const CountrySelect: React.FC<IProps> = ({
  label,
  required,
  error,
  value,
  onChange,
  onBlur,
  name,
  allowedCountries,
}) => {
  const countryOptions = useMemo(
    () =>
      filterByAllowedCountries(countryList, (country) => country.two_digit_code, allowedCountries).map((country) => ({
        value: country.two_digit_code,
        label: country.name,
      })),
    [allowedCountries],
  );

  const handleChange = useCallback(
    (selectedValue: ISelectorValue | null) => {
      onChange?.(selectedValue);
    },
    [onChange],
  );

  const selectedCountry = useMemo(() => {
    return countryOptions.find((option) => option.value === value) || null;
  }, [value, countryOptions]);

  // Browser autofill types a country name into the input without picking an option (EX-6500).
  const selectTypedCountry = useCallback(
    (text: string) => {
      const option = findCountryOption(text, countryOptions);

      if (option && option.value !== value) {
        onChange?.(option);
      }
    },
    [countryOptions, value, onChange],
  );

  // Autofill fills fields the user isn't in, so resolve those at once. While the user is typing
  // here, wait for blur so that "Uk" isn't snapped to the UK on the way to "Ukraine".
  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, text: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'input' && event?.target !== document.activeElement) {
        selectTypedCountry(text);
      }
    },
    [selectTypedCountry],
  );

  const [isOpen, setIsOpen] = useState(false);

  // The Autocomplete opens on every input change. Keep it closed when autofill wrote a country we
  // could select, so it doesn't cover the fields below; open it for anything else.
  const handleOpen = useCallback(
    (event: React.SyntheticEvent) => {
      const target = event.target as HTMLInputElement;
      const isAutofilled = event.type === 'change' && target !== document.activeElement;

      if (!isAutofilled || !findCountryOption(target.value, countryOptions)) {
        setIsOpen(true);
      }
    },
    [countryOptions],
  );

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      selectTypedCountry(event.target.value);
      onBlur?.(event);
    },
    [selectTypedCountry, onBlur],
  );

  return (
    <Selector
      name={name}
      value={selectedCountry}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      options={countryOptions}
      required={required}
      error={error}
      dataAction="input_country"
      label={label}
      autoComplete="country"
    />
  );
};
